import type { AnimationProperties, AnimationType, Bounds } from '@/types';

/**
 * Easing functions for smooth animations
 */
const easings = {
    // Ease out cubic - smooth deceleration
    easeOutCubic: (t: number): number => 1 - Math.pow(1 - t, 3),
    // Ease in cubic - smooth acceleration
    easeInCubic: (t: number): number => t * t * t,
    // Ease in out cubic - smooth both ends
    easeInOutCubic: (t: number): number => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
    // Bounce effect
    easeOutBounce: (t: number): number => {
        const n1 = 7.5625;
        const d1 = 2.75;
        if (t < 1 / d1) {
            return n1 * t * t;
        } else if (t < 2 / d1) {
            return n1 * (t -= 1.5 / d1) * t + 0.75;
        } else if (t < 2.5 / d1) {
            return n1 * (t -= 2.25 / d1) * t + 0.9375;
        } else {
            return n1 * (t -= 2.625 / d1) * t + 0.984375;
        }
    },
    // Elastic bounce
    easeOutElastic: (t: number): number => {
        if (t === 0 || t === 1) return t;
        return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
    },
};

/**
 * Animation transform result
 */
export interface AnimationTransform {
    opacity: number;
    translateX: number;
    translateY: number;
    scale: number;
    rotation: number; // Additional rotation from animation
}

/**
 * Calculate the animation transform for an overlay at a specific time
 */
export function calculateAnimationTransform(
    animation: AnimationProperties | undefined,
    currentTime: number,
    startTime: number,
    endTime: number,
    bounds: Bounds
): AnimationTransform {
    // Default transform (no animation)
    const defaultTransform: AnimationTransform = {
        opacity: 1,
        translateX: 0,
        translateY: 0,
        scale: 1,
        rotation: 0,
    };

    if (!animation) return defaultTransform;

    const overlayDuration = endTime - startTime;
    const timeInOverlay = currentTime - startTime;

    let transform = { ...defaultTransform };

    // Calculate entry animation
    const entryStart = animation.entryDelay;
    const entryEnd = entryStart + animation.entryDuration;

    if (timeInOverlay < entryEnd && animation.entryType !== 'none') {
        const entryProgress = Math.max(0, Math.min(1, (timeInOverlay - entryStart) / animation.entryDuration));
        const easedProgress = easings.easeOutCubic(entryProgress);

        transform = applyAnimationType(
            animation.entryType,
            easedProgress,
            bounds,
            true // isEntry
        );
    }

    // Calculate exit animation
    const exitStart = overlayDuration - animation.exitDuration;

    if (timeInOverlay >= exitStart && animation.exitType !== 'none') {
        const exitProgress = Math.max(0, Math.min(1, (timeInOverlay - exitStart) / animation.exitDuration));
        const easedProgress = easings.easeInCubic(exitProgress);

        transform = applyAnimationType(
            animation.exitType,
            1 - easedProgress, // Reverse progress for exit
            bounds,
            false // isExit
        );
    }

    // Apply pulse animation if enabled
    if (animation.pulseEnabled && timeInOverlay >= entryEnd && timeInOverlay < exitStart) {
        const pulsePhase = (timeInOverlay * animation.pulseSpeed * 2) % 1;
        const pulseValue = Math.sin(pulsePhase * Math.PI * 2) * 0.05 + 1; // Subtle 5% scale pulse
        transform.scale *= pulseValue;
    }

    return transform;
}

/**
 * Apply animation type and return the transform values
 */
function applyAnimationType(
    type: AnimationType,
    progress: number,
    _bounds: Bounds,
    isEntry: boolean
): AnimationTransform {
    const slideDistance = 100; // pixels

    switch (type) {
        case 'fade':
            return {
                opacity: progress,
                translateX: 0,
                translateY: 0,
                scale: 1,
                rotation: 0,
            };

        case 'slide-left':
            return {
                opacity: progress,
                translateX: isEntry ? -slideDistance * (1 - progress) : slideDistance * (1 - progress),
                translateY: 0,
                scale: 1,
                rotation: 0,
            };

        case 'slide-right':
            return {
                opacity: progress,
                translateX: isEntry ? slideDistance * (1 - progress) : -slideDistance * (1 - progress),
                translateY: 0,
                scale: 1,
                rotation: 0,
            };

        case 'slide-up':
            return {
                opacity: progress,
                translateX: 0,
                translateY: isEntry ? -slideDistance * (1 - progress) : slideDistance * (1 - progress),
                scale: 1,
                rotation: 0,
            };

        case 'slide-down':
            return {
                opacity: progress,
                translateX: 0,
                translateY: isEntry ? slideDistance * (1 - progress) : -slideDistance * (1 - progress),
                scale: 1,
                rotation: 0,
            };

        case 'scale':
            return {
                opacity: progress,
                translateX: 0,
                translateY: 0,
                scale: 0.5 + (progress * 0.5), // Scale from 50% to 100%
                rotation: 0,
            };

        case 'bounce':
            const bounceProgress = easings.easeOutBounce(progress);
            return {
                opacity: progress,
                translateX: 0,
                translateY: isEntry ? -50 * (1 - bounceProgress) : 50 * (1 - bounceProgress),
                scale: 1,
                rotation: 0,
            };

        case 'none':
        default:
            return {
                opacity: 1,
                translateX: 0,
                translateY: 0,
                scale: 1,
                rotation: 0,
            };
    }
}

/**
 * Get a human-readable label for animation type
 */
export function getAnimationLabel(type: AnimationType): string {
    const labels: Record<AnimationType, string> = {
        'none': 'None',
        'fade': 'Fade',
        'slide-left': 'Slide Left',
        'slide-right': 'Slide Right',
        'slide-up': 'Slide Up',
        'slide-down': 'Slide Down',
        'scale': 'Scale/Zoom',
        'bounce': 'Bounce',
    };
    return labels[type] || type;
}

/**
 * List of all available animation types
 */
export const ANIMATION_TYPES: AnimationType[] = [
    'none',
    'fade',
    'slide-left',
    'slide-right',
    'slide-up',
    'slide-down',
    'scale',
    'bounce',
];

